/**
 * Explicit open/close state for header dropdown menus.
 *
 * Replaces the DaisyUI CSS-only pattern (`.dropdown` + `tabIndex={0}` on both
 * the trigger and `.dropdown-content`, shown via `:focus-within`). That pattern
 * has no notion of "an item was chosen": clicking an entry inside the panel
 * keeps focus within the `.dropdown`, so `:focus-within` stays true and the
 * menu stays open after the user picked something (QA 17. 9. 2026).
 *
 * This hook owns the state instead, so choosing an item closes the menu. It
 * also closes on Escape (returning focus to the trigger, so tab order is not
 * lost to the document body), on a pointer press outside, and when focus moves
 * out of the menu entirely — e.g. tabbing past the last item.
 *
 * Consumers must render the panel only while `open` is true. Keeping it in the
 * DOM while closed would put its items back in the tab order, which is exactly
 * what the CSS-only version got wrong.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

export interface DismissableMenu {
	/** Whether the panel should be rendered. */
	open: boolean
	/** Container element ref — wraps both trigger and panel; used for hit-testing. */
	containerRef: React.RefObject<HTMLDivElement | null>
	/** Trigger element ref — focus returns here when the menu closes via Escape. */
	triggerRef: React.RefObject<HTMLButtonElement | null>
	/** Toggle the menu. Bind to the trigger's `onClick`. */
	toggle: () => void
	/** Close the menu. Call from every item's `onClick`/`onSelect`. */
	close: () => void
	/** Close and move focus back to the trigger (Escape, and item activation via keyboard). */
	closeAndRefocus: () => void
	/**
	 * Spread onto the trigger element. `aria-expanded` tracks the real state.
	 * Deliberately no `aria-haspopup="menu"`: these panels are plain link/button
	 * lists, not ARIA menus with roving arrow-key focus, and announcing a role
	 * the panel does not implement would be worse than announcing none.
	 */
	triggerProps: {
		'aria-expanded': boolean
		onClick: () => void
		onKeyDown: (event: React.KeyboardEvent) => void
	}
}

export function useDismissableMenu(): DismissableMenu {
	const [open, setOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement | null>(null)
	const triggerRef = useRef<HTMLButtonElement | null>(null)

	const close = useCallback(() => {
		setOpen(false)
	}, [])

	const closeAndRefocus = useCallback(() => {
		setOpen(false)
		triggerRef.current?.focus()
	}, [])

	const toggle = useCallback(() => {
		setOpen((previous) => !previous)
	}, [])

	useEffect(() => {
		if (!open) {
			return
		}

		const isInside = (target: EventTarget | null) =>
			target instanceof Node && containerRef.current?.contains(target) === true

		const onPointerDown = (event: PointerEvent) => {
			if (!isInside(event.target)) {
				setOpen(false)
			}
		}

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.stopPropagation()
				setOpen(false)
				// Keep the caret somewhere sensible: focus goes back to the
				// trigger rather than being dropped on <body>, which would
				// restart tabbing from the top of the document.
				triggerRef.current?.focus()
			}
		}

		// Tabbing out of the panel should close it too, but without stealing
		// focus back — the user is on their way somewhere else.
		const onFocusIn = (event: FocusEvent) => {
			if (!isInside(event.target)) {
				setOpen(false)
			}
		}

		document.addEventListener('pointerdown', onPointerDown, true)
		document.addEventListener('keydown', onKeyDown)
		document.addEventListener('focusin', onFocusIn)

		return () => {
			document.removeEventListener('pointerdown', onPointerDown, true)
			document.removeEventListener('keydown', onKeyDown)
			document.removeEventListener('focusin', onFocusIn)
		}
	}, [open])

	const onTriggerKeyDown = useCallback((event: React.KeyboardEvent) => {
		// ArrowDown is the conventional "open it" key for a disclosure trigger.
		if (event.key === 'ArrowDown') {
			event.preventDefault()
			setOpen(true)
		}
	}, [])

	return {
		open,
		containerRef,
		triggerRef,
		toggle,
		close,
		closeAndRefocus,
		triggerProps: {
			'aria-expanded': open,
			onClick: toggle,
			onKeyDown: onTriggerKeyDown,
		},
	}
}
